import { State } from '@progress/kendo-data-query';

import { ColumnState } from 'core/ui';
import { ModelTypeInference } from 'core/utils/model-type-inference';

import { SortOption } from '../types/WorklistSidebarProps';

export const WorklistSortService = {
  /**
   * Generate sort options dynamically from a model type and visible columns
   * This approach uses TypeScript type inference to eliminate hardcoded field mappings
   */
  generateSortOptionsFromModel(
    columnsState: Record<string, ColumnState> | null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sampleModel: Record<string, any>,
  ): SortOption[] {
    if (!columnsState || !sampleModel) {
      // Fallback to basic ID sorting if no data available
      return [
        { label: 'Newest First', field: 'id', direction: 'desc', isDefault: true },
        { label: 'Oldest First', field: 'id', direction: 'asc' },
      ];
    }

    // Get visible fields from columns state
    const visibleFields = Object.entries(columnsState)
      .filter(([, state]) => state.show !== false)
      .map(([field]) => field)
      .filter((field) => field in sampleModel) // Ensure field exists in model
      .sort((a, b) => {
        // Sort by order index if available, otherwise alphabetically
        const orderA = columnsState[String(a)]?.orderIndex ?? 999;
        const orderB = columnsState[String(b)]?.orderIndex ?? 999;
        return orderA - orderB;
      });

    // Use the model type inference utility to generate sort options
    return ModelTypeInference.generateSortOptionsFromModel(sampleModel, visibleFields);
  },

  /**
   * Get the current sort option from the grid's data state
   */
  getCurrentSortOption(dataState: State | null, sortOptions: SortOption[]): SortOption | undefined {
    if (!dataState?.sort || dataState.sort.length === 0) {
      return sortOptions.find((opt) => opt.isDefault);
    }

    const currentSort = dataState.sort[0]; // Use the first sort descriptor
    return sortOptions.find((opt) => opt.field === currentSort.field && opt.direction === currentSort.dir);
  },

  /**
   * Apply sort option to data state
   */
  applySortToDataState(dataState: State, sortOption: SortOption): State {
    return {
      ...dataState,
      sort: [{ field: sortOption.field, dir: sortOption.direction }],
    };
  },

  /**
   * Create a sort option from current data state (for backward compatibility)
   */
  createSortOptionFromDataState(dataState: State | null): SortOption | null {
    if (!dataState?.sort || dataState.sort.length === 0) {
      return null;
    }

    const sort = dataState.sort[0];
    const label = ModelTypeInference.generateFieldLabel(sort.field);

    // Create a minimal model sample for type inference
    const sampleModel = { [sort.field]: null };
    const fieldType = ModelTypeInference.inferFieldType(sampleModel, sort.field);

    let directionLabel: string;
    switch (fieldType) {
      case 'date':
        directionLabel = sort.dir === 'asc' ? 'Oldest' : 'Newest';
        break;
      case 'numeric':
        directionLabel = sort.dir === 'asc' ? 'Low to High' : 'High to Low';
        break;
      case 'boolean':
        directionLabel = sort.dir === 'asc' ? 'No First' : 'Yes First';
        break;
      case 'text':
      default:
        directionLabel = sort.dir === 'asc' ? 'A-Z' : 'Z-A';
        break;
    }

    return {
      label: `${label} (${directionLabel})`,
      field: sort.field,
      direction: sort.dir as 'asc' | 'desc',
    };
  },

  /**
   * Get legacy sort criteria for backward compatibility with patient files
   */
  getLegacySortCriteria(sortOption: SortOption | undefined): 'Newest' | 'Oldest' | 'Name' {
    if (!sortOption) return 'Newest';

    // Create a minimal model sample for type inference
    const sampleModel = { [sortOption.field]: null };
    const fieldType = ModelTypeInference.inferFieldType(sampleModel, sortOption.field);

    // Check if it's a name-like field
    if (fieldType === 'text' && /name|title|description/i.test(String(sortOption.field))) {
      return 'Name';
    }

    // For date and numeric fields, use direction
    if (sortOption.direction === 'asc') {
      return 'Oldest';
    }

    return 'Newest';
  },
};
